import * as React from "react";
import StyledModal from "styled-react-modal";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { capitalize } from "lodash";
import { useDispatch } from "redux-react-hook";

import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import LoadingSpinner from "src/library/components/LoadingSpinner/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import { Input } from "src/library/components/Forms";
import useTerminology from "src/core/shared/hooks/useTerminology";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import { toggleJobsQueue } from "src/core/shared/store/global/global.actions";

import { useConnectorApi } from "../api/connector.api";

interface IProps {
  onDismiss: () => void;
  open: boolean;
}
/**
 * Modal allowing user to add a part number
 * that will trigger an import from the api
 */
export const ConnectorProductImportModal: React.ReactFCWithChildren<IProps> = props => {
  const [ successMessage, setSuccessMessage ] = React.useState<string | null>(null);
  const [ partNumber, setProductCode ] = React.useState<string | null>(null);
  const terminology = useTerminology();
  const [ syncProductResponse, syncProductRequest ] = useConnectorApi("IMPORT_API_PRODUCT");
  const [ { globalSettings } ] = useGlobalSettings();
  const dispatch = useDispatch();

  /** Display a success or error message from the api response */
  React.useEffect(() => {
    if (syncProductResponse.data) {
      setSuccessMessage("Import has been added to the queue!");
    } else if (syncProductResponse.error) {
      toast.error(syncProductResponse.error.message);
    }
  }, [ syncProductResponse ]);

  /** Handles the on submit of the form and sends the request to the api */
  const handleImport = React.useCallback(() => {
    if (partNumber) {
      syncProductRequest({ partNumber });
    } else {
      toast.error(`Please enter the ${terminology?.partNumber.singular.text} before submitting`);
    }
  }, [
    partNumber,
    syncProductRequest,
    terminology?.partNumber.singular.text
  ]);

  return (
    <ModalWrapper
      isOpen={props.open}
      onBackgroundClick={() => props.onDismiss()}
    >
      <ModalInner>
        <Icon
          className="close-icon"
          icon={Icons.CLOSE}
          onClick={() => props.onDismiss()}
        />

        <Text
          className="modal-title"
          as="h2"
          fontSize="h2"
        >
          {`Import ${terminology?.product.singular.text} from ${capitalize(globalSettings?.erp.name)}`}
        </Text>

        {syncProductResponse.pending && (
          <div>
            <LoadingSpinner
              type="circular"
              message={`Importing ${terminology?.product.singular.text}`}
              position="inline"
            />
          </div>
        )}

        {syncProductResponse.data && successMessage && (
          <div className="response-container">
            <Text>
              {successMessage}
            </Text>

            <Text as="p">
              {!!syncProductResponse.data && (
                <Button
                  $buttonType="plain"
                  onClick={() => dispatch(toggleJobsQueue(true))}
                >
                  View job queue
                </Button>
              )}
            </Text>

            <ButtonContainer>
              <Button
                onClick={() => {
                  setSuccessMessage(null);
                }}
              >
                Start Again
              </Button>

              <Button onClick={props.onDismiss}>
                Done
              </Button>
            </ButtonContainer>
          </div>
        )}

        {successMessage === null && (
          <div className="import-form">
            <Input
              as="input"
              label={`${capitalize(globalSettings?.erp.name)} ${terminology?.partNumber.singular.text} (comma separate to import multiple)`}
              name="partNumber"
              handleChange={e => setProductCode(e.target.value)}
            />

            <Button
              disabled={!partNumber}
              loading={syncProductResponse.pending}
              onClick={() => handleImport()}
            >
              Import
            </Button>
          </div>
        )}

      </ModalInner>
    </ModalWrapper>
  );
};

const ModalInner = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
    padding-top: 20px;
    position: relative;

    .close-icon {
      position: absolute;
      top: 5px;
      right: 5px;
    }

    .import-form {
      button {
        margin-top: ${theme.spacing.margins.md};
      }
    }

    .response-container {
      margin: ${theme.spacing.margins.md} 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  `}
`;

const ModalWrapper = StyledModal.styled`
    position: relative;
    background: white;
    width: 500px;
    border-radius: 1rem;
    max-width: 100vw;
    min-height: 100px;
    opacity: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;

  button {
    margin-right: 10px;
  }
`;
